import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api, canSee, isAdminRole } from "@/lib/api";
import { TOUR_EVENT, TOUR_STEPS } from "@/lib/tour";
import { TourBalloon, balloonPos } from "@/components/tour/TourBalloon";

const isMobile = () => window.innerWidth < 768;
const selectorsOf = (s) => (isMobile() && s.mobile ? s.mobile : s.targets);
const shown = (el) => el && el.getClientRects().length > 0;
const findEls = (s) => selectorsOf(s).map((q) => document.querySelector(q)).filter(shown);

function unionRect(els) {
  const rs = els.map((e) => e.getBoundingClientRect());
  const top = Math.max(6, Math.min(...rs.map((r) => r.top)) - 8);
  const left = Math.max(6, Math.min(...rs.map((r) => r.left)) - 8);
  const bottom = Math.min(window.innerHeight - 6, Math.max(...rs.map((r) => r.bottom)) + 8);
  const right = Math.min(window.innerWidth - 6, Math.max(...rs.map((r) => r.right)) + 8);
  return { top, left, width: right - left, height: Math.max(0, bottom - top), bottom };
}

function Spotlight({ rect }) {
  if (!rect) return <motion.div className="absolute inset-0 bg-slate-950/70" initial={{ opacity: 0 }} animate={{ opacity: 1 }} />;
  return (
    <motion.div className="absolute rounded-xl ring-2 ring-primary" style={{ boxShadow: "0 0 0 9999px rgba(2, 6, 23, 0.72)" }}
      initial={false} animate={{ top: rect.top, left: rect.left, width: rect.width, height: rect.height }} transition={{ type: "spring", stiffness: 260, damping: 30 }}
      data-testid="tour-spotlight">
      <span className="absolute inset-0 animate-pulse rounded-xl ring-4 ring-primary/30" />
    </motion.div>
  );
}

export function GuidedTour() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [steps, setSteps] = useState(null);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [ready, setReady] = useState(false);
  const dir = useRef(1);
  const step = steps?.[i];
  const artigo = useRef(null); // guia da ajuda: id do artigo, para a estatística (sem identificar ninguém)
  const evento = (tipo, passo) => { if (artigo.current) api.post("/help/evento", { tipo, artigo: artigo.current, passo }).catch(() => {}); };
  const end = useCallback((concluiu = false) => {
    if (artigo.current) evento(concluiu ? "guia_fim" : "guia_abandono", concluiu ? undefined : i);
    artigo.current = null;
    setJanelaAberta(false);
    setSteps(null); setRect(null); setReady(false);
  }, [i]); // eslint-disable-line react-hooks/exhaustive-deps
  const go = useCallback((d) => {
    dir.current = d;
    const n = i + d;
    if (n >= steps.length) end(true);
    else if (n >= 0) setI(n);
  }, [i, steps, end]);
  // Recomeçar não fecha nem desfaz nada na tela: se houver uma janela aberta, o 1º passo pede para a pessoa fechá-la ela mesma
  const [janelaAberta, setJanelaAberta] = useState(false);
  const recomecar = useCallback(() => {
    setJanelaAberta(!!document.querySelector('[role="dialog"][data-state="open"]'));
    dir.current = 1; setRect(null); setI(0);
  }, []);
  useEffect(() => {
    const start = (e) => {
      dir.current = 1; setRect(null); setI(0);
      const guia = e?.detail?.steps;
      artigo.current = guia ? e.detail.artigo || null : null;
      if (artigo.current) api.post("/help/evento", { tipo: "guia_inicio", artigo: artigo.current }).catch(() => {});
      setSteps(guia ? guia.map((s) => ({ ...s, guia: true })) : TOUR_STEPS.filter((s) => !s.area || canSee(session.user, s.area)));
    };
    window.addEventListener(TOUR_EVENT, start);
    return () => window.removeEventListener(TOUR_EVENT, start);
  }, [session.user]);
  useEffect(() => {
    if (!step) return undefined;
    setReady(false);
    if (step.guia && (step.admin ? !isAdminRole(session.user) : step.area && !canSee(session.user, step.area))) {
      setRect(null); setReady(true); return undefined; // sem permissão: não navega; o balão explica no centro
    }
    if (step.route && pathname + search !== step.route && pathname !== step.route) {
      // o guia só troca de tela no 1º passo: no meio dele, trocar de tela fecharia a janela que o usuário está preenchendo
      if (step.guia && i > 0) { setRect(null); setReady(true); return undefined; }
      navigate(step.route); return undefined;
    }
    if (!step.targets) { setRect(null); setReady(true); return undefined; }
    let stop = false;
    let tries = 0;
    const find = () => {
      if (stop) return;
      const els = findEls(step);
      if (els.length) {
        els[0].scrollIntoView({ block: "center", behavior: "smooth" });
        setTimeout(() => { if (!stop) { setRect(unionRect(findEls(step))); setReady(true); } }, 420);
      } else if (++tries > (step.guia ? 60 : 25)) {
        // no guia, se o botão não aparecer (sem permissão, tela diferente), mostra a explicação no centro em vez de pular
        if (step.guia) { setRect(null); setReady(true); } else go(dir.current === -1 && i === 0 ? 1 : dir.current);
      }
      else setTimeout(find, 120);
    };
    find();
    return () => { stop = true; };
  }, [step, pathname, search, navigate, go, i, session.user]);
  // passo "clique": o usuário clica no próprio botão destacado (a tela funciona normalmente) e o guia avança
  useEffect(() => {
    if (!ready || !step?.clique || !rect) return undefined;
    const els = findEls(step);
    const avancar = () => setTimeout(() => go(1), 250);
    els.forEach((el) => el.addEventListener("click", avancar, { once: true }));
    return () => els.forEach((el) => el.removeEventListener("click", avancar));
  }, [ready, step, rect, go]);
  useEffect(() => {
    if (!ready || !step?.targets) return undefined;
    const sync = () => { const els = findEls(step); if (els.length) setRect(unionRect(els)); };
    window.addEventListener("resize", sync);
    window.addEventListener("scroll", sync, true);
    return () => { window.removeEventListener("resize", sync); window.removeEventListener("scroll", sync, true); };
  }, [ready, step]);
  useEffect(() => {
    if (!step) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") end(false);
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft" && i > 0 && !step.guia) go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [step, i, go, end]);
  if (!step) return null;
  const semAcesso = step.guia && (step.admin ? !isAdminRole(session.user) : step.area && !canSee(session.user, step.area));
  const base = semAcesso ? "Seu usuário não tem acesso a esta tela. Peça ao administrador." : (isMobile() && step.mtext ? step.mtext : step.text);
  const text = step.guia && i === 0 && janelaAberta ? `Antes, feche a janela que está aberta (use "Cancelar" se não quiser salvar o que digitou). ${base}` : base;
  return createPortal(
    <div className={`fixed inset-0 z-[60] ${step.clique && rect ? "pointer-events-none" : ""}`} data-testid="tour-overlay">
      <Spotlight rect={ready ? rect : null} />
      <AnimatePresence mode="wait">
        {ready && <TourBalloon key={i} step={step} text={step.clique && rect ? `${text} (Clique no destaque para continuar.)` : text} index={i} total={steps.length} pos={balloonPos(rect, window.innerWidth, window.innerHeight)}
          onNext={() => go(1)} onBack={() => go(-1)} onSkip={() => end(false)} onRestart={recomecar} />}
      </AnimatePresence>
    </div>,
    document.body,
  );
}
